(() => {
  const money = (cents) => {
    if (typeof cents !== 'number') return '';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return `R$ ${value.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  class BalaoHeader {
    constructor(root) {
      this.root = root;
      this.bar = root.querySelector('[data-balao-header-bar]');
      this.menuToggle = root.querySelector('[data-balao-menu-toggle]');
      this.drawer = root.querySelector('[data-balao-drawer]');
      this.overlay = root.querySelector('[data-balao-overlay]');
      this.drawerClose = root.querySelector('[data-balao-drawer-close]');
      this.searchToggle = root.querySelector('[data-balao-search-toggle]');
      this.searchPanel = root.querySelector('[data-balao-search]');
      this.searchClose = root.querySelector('[data-balao-search-close]');
      this.searchInput = root.querySelector('[data-balao-search-input]');
      this.searchResults = root.querySelector('[data-balao-search-results]');
      this.cartBadge = root.querySelector('[data-balao-cart-badge]');
      this.cartCount = root.querySelector('[data-balao-cart-count]');
      this.cartLink = root.querySelector('[data-balao-cart-link]');
      this.promos = [...root.querySelectorAll('[data-balao-header-promo]')];
      this.promoWrap = root.querySelector('[data-balao-header-promos]');
      this.promoTimer = null;
      this.searchTimer = null;
      this.lastScrollY = window.scrollY || 0;
      this.spacer = null;

      this.#ensureSpacer();
      this.#bind();
      this.#startPromos();
      this.#onScroll();
    }

    #ensureSpacer() {
      const section = this.root.closest('.shopify-section') || this.root.parentElement;
      if (!section) return;

      this.spacer = section.querySelector('[data-balao-header-spacer]');
      if (!this.spacer) {
        this.spacer = document.createElement('div');
        this.spacer.setAttribute('data-balao-header-spacer', '');
        this.spacer.className = 'balao-site-header__spacer';
        this.spacer.setAttribute('aria-hidden', 'true');
        section.insertBefore(this.spacer, this.root);
      }

      this.#syncSpacer();

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.#syncSpacer());
        this.ro.observe(this.root);
      }
    }

    #syncSpacer() {
      if (!this.spacer) return;
      this.spacer.style.height = `${this.root.offsetHeight}px`;
      document.body.style.setProperty('--header-height', `${this.root.offsetHeight}px`);
      document.body.style.setProperty('--header-group-height', `${this.root.offsetHeight}px`);
    }

    #bind() {
      window.addEventListener('scroll', () => this.#onScroll(), { passive: true });

      this.menuToggle?.addEventListener('click', () => this.#toggleDrawer());
      this.drawerClose?.addEventListener('click', () => this.#closeDrawer());
      this.overlay?.addEventListener('click', () => {
        this.#closeDrawer();
        this.#closeSearch();
      });

      this.searchToggle?.addEventListener('click', () => this.#toggleSearch());
      this.searchClose?.addEventListener('click', () => this.#closeSearch());
      this.searchInput?.addEventListener('input', () => this.#onSearchInput());

      this.cartLink?.addEventListener('click', (event) => {
        const drawer = document.querySelector('theme-drawer#cart-drawer, cart-drawer-component, #cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          event.preventDefault();
          drawer.open();
        }
      });

      document.addEventListener('cart:refresh', () => this.#refreshCart());
      document.addEventListener('cart:update', () => this.#refreshCart());
      document.addEventListener('balao:cart:added', () => this.#refreshCart(true));

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.#closeDrawer();
          this.#closeSearch();
        }
      });
    }

    #onScroll() {
      const y = window.scrollY || 0;
      const scrolled = y > 12;
      const delta = y - this.lastScrollY;
      const nearTop = y < 80;

      this.root.classList.toggle('is-scrolled', scrolled);

      // Esconde ao descer; mostra ao subir (ou no topo)
      if (nearTop || this.root.classList.contains('is-drawer-open') || this.searchPanel?.hidden === false) {
        this.root.classList.remove('is-header-hidden');
      } else if (delta > 8 && y > 120) {
        this.root.classList.add('is-header-hidden');
      } else if (delta < -8) {
        this.root.classList.remove('is-header-hidden');
      }

      this.lastScrollY = y;
    }

    #startPromos() {
      if (this.promos.length < 2 || !this.promoWrap) return;
      const interval = Math.max(3, Number(this.promoWrap.dataset.interval || 5)) * 1000;
      let index = 0;
      this.promoTimer = window.setInterval(() => {
        this.promos[index]?.classList.remove('is-active');
        index = (index + 1) % this.promos.length;
        this.promos[index]?.classList.add('is-active');
      }, interval);
    }

    #toggleDrawer() {
      if (this.root.classList.contains('is-drawer-open')) this.#closeDrawer();
      else this.#openDrawer();
    }

    #openDrawer() {
      this.#closeSearch();
      this.root.classList.add('is-drawer-open');
      this.menuToggle?.setAttribute('aria-expanded', 'true');
      this.menuToggle?.setAttribute('aria-label', 'Fechar menu');
      this.drawer?.setAttribute('aria-hidden', 'false');
      if (this.overlay) this.overlay.hidden = false;
      document.body.classList.add('balao-header-lock');
    }

    #closeDrawer() {
      this.root.classList.remove('is-drawer-open');
      this.menuToggle?.setAttribute('aria-expanded', 'false');
      this.menuToggle?.setAttribute('aria-label', 'Abrir menu');
      this.drawer?.setAttribute('aria-hidden', 'true');
      if (this.overlay && this.searchPanel?.hidden !== false) this.overlay.hidden = true;
      if (this.searchPanel?.hidden !== false) document.body.classList.remove('balao-header-lock');
    }

    #toggleSearch() {
      if (this.searchPanel?.hidden === false) this.#closeSearch();
      else this.#openSearch();
    }

    #openSearch() {
      this.#closeDrawer();
      if (!this.searchPanel) return;
      this.searchPanel.hidden = false;
      this.searchToggle?.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => this.searchInput?.focus(), 30);
    }

    #closeSearch() {
      if (!this.searchPanel) return;
      this.searchPanel.hidden = true;
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      if (this.searchResults) {
        this.searchResults.hidden = true;
        this.searchResults.innerHTML = '';
      }
      if (!this.root.classList.contains('is-drawer-open')) {
        if (this.overlay) this.overlay.hidden = true;
        document.body.classList.remove('balao-header-lock');
      }
    }

    #onSearchInput() {
      window.clearTimeout(this.searchTimer);
      const query = (this.searchInput?.value || '').trim();
      if (query.length < 2) {
        if (this.searchResults) {
          this.searchResults.hidden = true;
          this.searchResults.innerHTML = '';
        }
        return;
      }
      this.searchTimer = window.setTimeout(() => this.#fetchResults(query), 220);
    }

    async #fetchResults(query) {
      if (!this.searchResults) return;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('search_failed');
        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        if (!products.length) {
          this.searchResults.hidden = false;
          this.searchResults.innerHTML = '<p class="balao-site-header__result-empty">Nenhum produto encontrado.</p>';
          return;
        }
        this.searchResults.hidden = false;
        this.searchResults.innerHTML = products
          .map((product) => {
            const image = product.image || product.featured_image || '';
            const rawPrice = Number(product.price);
            const priceLabel = Number.isFinite(rawPrice) ? money(rawPrice) : '';
            const safeTitle = String(product.title || '').replace(/</g, '&lt;');
            return `<a class="balao-site-header__result" href="${product.url}">
              ${image ? `<img class="balao-site-header__result-thumb" src="${image}" alt="" width="52" height="52" loading="lazy">` : '<span class="balao-site-header__result-thumb"></span>'}
              <span>
                <p class="balao-site-header__result-title">${safeTitle}</p>
                ${priceLabel ? `<p class="balao-site-header__result-price">${priceLabel}</p>` : ''}
              </span>
            </a>`;
          })
          .join('');
      } catch (error) {
        this.searchResults.hidden = false;
        this.searchResults.innerHTML = '<p class="balao-site-header__result-empty">Não foi possível buscar agora.</p>';
      }
    }

    async #refreshCart(pulse = false) {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        const count = cart.item_count || 0;
        if (this.cartCount) this.cartCount.textContent = String(count);
        if (this.cartBadge) {
          this.cartBadge.classList.toggle('is-empty', count === 0);
          this.cartBadge.setAttribute('aria-hidden', count === 0 ? 'true' : 'false');
          if (pulse || count > 0) {
            this.cartBadge.classList.remove('is-pulse');
            void this.cartBadge.offsetWidth;
            this.cartBadge.classList.add('is-pulse');
          }
        }
      } catch (error) {
        /* ignore */
      }
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-balao-header]').forEach((root) => {
      if (root.dataset.balaoHeaderReady) return;
      root.dataset.balaoHeaderReady = 'true';
      new BalaoHeader(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
